import React, { createContext, useState, useEffect, useContext } from 'react';
import { User } from '../types';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserData = async (userId: string): Promise<User | null> => {
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user data:', error);
        return null;
      }

      if (!userData) {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) return null;

        // For Google auth, use the user's Google profile data
        const name = authUser.user_metadata.full_name || authUser.user_metadata.name || 'User';
        const avatar = authUser.user_metadata.avatar_url;

        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert([{
            id: userId,
            name,
            email: authUser.email,
            avatar,
            preferences: {
              theme: 'dark',
              notifications: true,
              weeklyDigest: true
            }
          }])
          .select()
          .single();

        if (insertError) {
          console.error('Error creating user:', insertError);
          return null;
        }

        return {
          id: newUser.id,
          email: newUser.email,
          name: newUser.name,
          createdAt: newUser.created_at,
          avatar: newUser.avatar,
          preferences: newUser.preferences
        };
      }

      return {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        createdAt: userData.created_at,
        avatar: userData.avatar,
        preferences: userData.preferences
      };
    } catch (err) {
      console.error('Error in fetchUserData:', err);
      return null;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          const userData = await fetchUserData(session.user.id);
          setUser(userData);
        } else {
          setUser(null);
        }

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          console.log('Auth event:', event);
          
          if (event === 'SIGNED_OUT') {
            setUser(null);
          } else if (session?.user) {
            const userData = await fetchUserData(session.user.id);
            setUser(userData);
          }

          // Handle OAuth callbacks
          if (event === 'SIGNED_IN' && session?.user?.app_metadata?.provider === 'google') {
            const userData = await fetchUserData(session.user.id);
            setUser(userData);
            toast.success('Successfully signed in with Google');
          }
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (err) {
        console.error('Auth initialization error:', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      if (data.user) {
        const userData = await fetchUserData(data.user.id);
        if (!userData) {
          throw new Error('Failed to fetch user data');
        }
        setUser(userData);
        toast.success('Successfully signed in');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during login';
      setError(errorMessage);
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name }
        }
      });

      if (signUpError) throw signUpError;

      if (data.user) {
        const userData = await fetchUserData(data.user.id);
        if (userData) {
          setUser(userData);
          toast.success('Successfully registered');
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during registration';
      setError(errorMessage);
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      setUser(null);
      window.location.href = '/';
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, register, logout, clearError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};